import {
  assertionSpecSchema,
  cachedRecipeOracleSchema,
  cachedDataScriptSchema,
  cacheReplayableActionTypeSchema,
  selectorStrategySchema,
} from './workflow-cache.js';

export type CacheActionContractInput = {
  actionType: unknown;
  args?: unknown;
  selectors?: unknown;
  assertion?: unknown;
  code?: unknown;
  recipeOracle?: unknown;
};

export type CacheActionContractIssue = {
  path: PropertyKey[];
  message: string;
};

export type CacheActionContractContext = {
  /** Stored data-script artifacts available to execute data actions. */
  dataScripts?: unknown;
};

const isRecordValue = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isNonBlankString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isPositiveIntegerValue = (value: unknown): boolean => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;
  return Number.isInteger(parsed) && parsed > 0;
};

const isFiniteNumberValue = (value: unknown): boolean => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;
  return Number.isFinite(parsed);
};

/**
 * Validate the executable portion of a cached action.
 *
 * This is deliberately a pure issue collector rather than a second action
 * type. Planned optimizer actions and stored cache actions have different
 * metadata, but the payload replay consumes must obey one contract at route
 * admission, final recipe construction, and replay trust validation.
 */
export function cacheActionContractIssues(
  input: unknown,
  context: CacheActionContractContext = {}
): CacheActionContractIssue[] {
  const issues: CacheActionContractIssue[] = [];
  const add = (path: PropertyKey[], message: string) => issues.push({ path, message });
  const validateDataScript = (script: unknown, scriptIndex: number) => {
    const parsed = cachedDataScriptSchema.safeParse(script);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        add(['dataScriptsJson', scriptIndex, ...issue.path], issue.message);
      }
    }
  };

  if (!isRecordValue(input)) {
    add([], 'Expected an action object');
    return issues;
  }
  const action: CacheActionContractInput = {
    actionType: input.actionType,
    args: input.args,
    selectors: input.selectors,
    assertion: input.assertion,
    code: input.code,
    recipeOracle: input.recipeOracle,
  };

  const actionTypeResult = cacheReplayableActionTypeSchema.safeParse(action.actionType);
  if (!actionTypeResult.success) {
    const actionType = String(action.actionType);
    const hint =
      actionType === 'fill'
        ? '; use "type" for fill steps'
        : actionType === 'select'
          ? '; use "select_option" for select steps'
          : '';
    add(['actionType'], `Action type "${actionType}" is not replayable${hint}`);
    return issues;
  }
  const actionType = actionTypeResult.data;

  if (action.recipeOracle !== undefined && actionType !== 'run_code') {
    add(['recipeOracle'], 'Recipe oracles are only valid on run_code actions');
  }

  const args = isRecordValue(action.args) ? action.args : null;
  if (!args) {
    add(['args'], 'Expected an object');
    return issues;
  }

  if (action.selectors !== undefined) {
    const parsed = selectorStrategySchema.safeParse(action.selectors);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        add(['selectors', ...issue.path], issue.message);
      }
    }
  }
  const requireSelectors = () => {
    if (action.selectors === undefined) {
      add(['selectors'], `Expected selectors for ${actionType}`);
    }
  };

  switch (actionType) {
    case 'navigate':
      if (args.direction !== 'back' && !isNonBlankString(args.url)) {
        add([], 'Navigate missing url and direction');
      }
      break;
    case 'click':
      if (args.doubleClick !== undefined && typeof args.doubleClick !== 'boolean') {
        add(['args', 'doubleClick'], 'Expected a boolean');
      }
      if (
        args.button !== undefined &&
        args.button !== 'left' &&
        args.button !== 'right' &&
        args.button !== 'middle'
      ) {
        add(['args', 'button'], 'Expected "left", "right", or "middle"');
      }
      if (
        args.modifiers !== undefined &&
        (!Array.isArray(args.modifiers) ||
          args.modifiers.some(
            (modifier) =>
              modifier !== 'Alt' &&
              modifier !== 'Control' &&
              modifier !== 'Meta' &&
              modifier !== 'Shift'
          ))
      ) {
        add(
          ['args', 'modifiers'],
          'Expected an array containing only "Alt", "Control", "Meta", or "Shift"'
        );
      }
      requireSelectors();
      break;
    case 'hover':
      requireSelectors();
      break;
    case 'type':
      requireSelectors();
      if (!isNonBlankString(args.text) && !isNonBlankString(args.value)) {
        add(['args', 'text'], 'Expected non-empty text or value');
      }
      if (args.submit !== undefined && typeof args.submit !== 'boolean') {
        add(['args', 'submit'], 'Expected a boolean');
      }
      break;
    case 'press_key':
      if (!isNonBlankString(args.key)) add(['args', 'key'], 'Expected a non-empty string');
      break;
    case 'select_option':
      requireSelectors();
      if (!isNonBlankString(args.value)) {
        add(['args', 'value'], 'Expected a non-empty string');
      }
      break;
    case 'fill_form': {
      const fields = args.fields;
      if (!Array.isArray(fields) || fields.length === 0) {
        add(['args', 'fields'], 'Expected a non-empty array');
        break;
      }
      for (const [fieldIndex, field] of fields.entries()) {
        const fieldPath: PropertyKey[] = ['args', 'fields', fieldIndex];
        if (!isRecordValue(field)) {
          add(fieldPath, 'Expected an object');
          continue;
        }
        if (!isNonBlankString(field.value)) {
          add([...fieldPath, 'value'], 'Expected a non-empty string');
        }
        if (field.selectors === undefined) {
          add([...fieldPath, 'selectors'], 'Expected selectors for fill_form field');
          continue;
        }
        const parsed = selectorStrategySchema.safeParse(field.selectors);
        if (!parsed.success) {
          for (const issue of parsed.error.issues) {
            add([...fieldPath, 'selectors', ...issue.path], issue.message);
          }
        }
      }
      break;
    }
    case 'resize':
      if (!isPositiveIntegerValue(args.width)) {
        add(['args', 'width'], 'Expected a positive integer');
      }
      if (!isPositiveIntegerValue(args.height)) {
        add(['args', 'height'], 'Expected a positive integer');
      }
      break;
    case 'scroll':
      if (args.mode === 'coordinates') {
        if (!isFiniteNumberValue(args.x)) add(['args', 'x'], 'Expected a finite number');
        if (!isFiniteNumberValue(args.y)) add(['args', 'y'], 'Expected a finite number');
      } else if (
        (args.mode === 'toRef' || args.mode === 'withinRef') &&
        action.selectors === undefined
      ) {
        add(['selectors'], `Expected selectors for scroll mode ${String(args.mode)}`);
      }
      break;
    case 'screenshot':
      break;
    case 'file_upload':
      if (
        !Array.isArray(args.paths) ||
        args.paths.length === 0 ||
        args.paths.some((path) => !isNonBlankString(path))
      ) {
        add(['args', 'paths'], 'Expected a non-empty array of file paths');
      }
      break;
    case 'move':
      if (typeof args.x !== 'number' || !Number.isFinite(args.x)) {
        add(['args', 'x'], 'Expected a finite number');
      }
      if (typeof args.y !== 'number' || !Number.isFinite(args.y)) {
        add(['args', 'y'], 'Expected a finite number');
      }
      break;
    case 'wait_for': {
      const hasCondition =
        (typeof args.timeSec === 'number' && Number.isFinite(args.timeSec) && args.timeSec > 0) ||
        isNonBlankString(args.text) ||
        isNonBlankString(args.textGone) ||
        isNonBlankString(args.selector);
      if (!hasCondition) {
        add([], 'Wait_for requires positive timeSec or non-empty text, textGone, or selector');
      }
      break;
    }
    case 'handle_dialog':
      if (args.action !== 'accept' && args.action !== 'dismiss') {
        add(['args', 'action'], 'Expected "accept" or "dismiss"');
      }
      if (args.promptText !== undefined && typeof args.promptText !== 'string') {
        add(['args', 'promptText'], 'Expected a string');
      }
      break;
    case 'annotation':
      break;
    case 'assert': {
      const parsed = assertionSpecSchema.safeParse(action.assertion);
      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          add(['assertion', ...issue.path], issue.message);
        }
      } else if (!parsed.data.condition.trim()) {
        add(['assertion', 'condition'], 'Expected a non-empty string');
      }
      break;
    }
    case 'capture':
      if (!isNonBlankString(args.expression)) {
        add(['args', 'expression'], 'Expected a non-empty string');
      }
      if (!isNonBlankString(args.saveAs)) {
        add(['args', 'saveAs'], 'Expected a non-empty string');
      }
      break;
    case 'extract_table':
      if (!isNonBlankString(args.name)) {
        add(['args', 'name'], 'Expected a non-empty string');
      } else {
        const dataScripts = Array.isArray(context.dataScripts) ? context.dataScripts : [];
        const scriptIndex = dataScripts.findIndex(
          (script) => isRecordValue(script) && script.tableName === args.name
        );
        if (scriptIndex < 0) {
          add(['dataScriptsJson'], `Expected a data script for table "${args.name}"`);
        } else {
          validateDataScript(dataScripts[scriptIndex], scriptIndex);
        }
      }
      break;
    case 'python_execute':
      if (!isNonBlankString(args.cacheLabel)) {
        add(['args', 'cacheLabel'], 'Expected a non-empty string');
      } else {
        const dataScripts = Array.isArray(context.dataScripts) ? context.dataScripts : [];
        const scriptIndex = dataScripts.findIndex(
          (script) => isRecordValue(script) && script.label === args.cacheLabel
        );
        if (scriptIndex < 0) {
          add(['dataScriptsJson'], `Expected a data script labeled "${args.cacheLabel}"`);
        } else {
          validateDataScript(dataScripts[scriptIndex], scriptIndex);
        }
      }
      break;
    case 'run_code':
      if (!isNonBlankString(action.code)) {
        add(['code'], 'Expected a non-empty string');
      } else if (action.code.length > 16_000) {
        add(['code'], 'Expected at most 16000 characters');
      }
      if (action.recipeOracle !== undefined) {
        const parsed = cachedRecipeOracleSchema.safeParse(action.recipeOracle);
        if (!parsed.success) {
          for (const issue of parsed.error.issues) {
            add(['recipeOracle', ...issue.path], issue.message);
          }
        }
        if (typeof action.code === 'string' && !action.code.includes('__evaluateRecipeOracle')) {
          add(['code'], 'Composite recipe code must invoke the server-authored oracle binding');
        }
      }
      break;
    case 'mcp_tool_call':
      // Minted exclusively by the mcp node lane (mcp-cache-save.ts) and
      // replayed by mcp-replay.ts. Browser recipe surfaces (optimizer commit,
      // CLI cache-recipe route) must not author it — the browser replay
      // runtime cannot execute it.
      add([], 'mcp_tool_call actions are minted by the mcp node lane and cannot be authored here');
      break;
  }

  return issues;
}
